import { useEffect } from "react";

/**
 * Lightweight SEO helper — sets document <title>, meta tags, Open Graph,
 * Twitter cards, canonical URL and JSON-LD structured data.
 *
 * Preferred to react-helmet for a CRA app without SSR — writes directly
 * to document.head on mount and cleans up on unmount.
 */
const SEO = ({ title, description, canonical, image, keywords, structuredData, type = "website" }) => {
  useEffect(() => {
    if (title) document.title = title;

    const ensureMeta = (selector, attrName, attrValue, content) => {
      if (!content) return null;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return el;
    };

    const nodes = [];
    nodes.push(ensureMeta('meta[name="description"]', "name", "description", description));
    nodes.push(ensureMeta('meta[name="keywords"]', "name", "keywords", keywords));
    nodes.push(ensureMeta('meta[property="og:title"]', "property", "og:title", title));
    nodes.push(ensureMeta('meta[property="og:description"]', "property", "og:description", description));
    nodes.push(ensureMeta('meta[property="og:image"]', "property", "og:image", image));
    nodes.push(ensureMeta('meta[property="og:type"]', "property", "og:type", type));
    nodes.push(ensureMeta('meta[property="og:url"]', "property", "og:url", canonical));
    nodes.push(ensureMeta('meta[name="twitter:card"]', "name", "twitter:card", image ? "summary_large_image" : "summary"));
    nodes.push(ensureMeta('meta[name="twitter:title"]', "name", "twitter:title", title));
    nodes.push(ensureMeta('meta[name="twitter:description"]', "name", "twitter:description", description));
    nodes.push(ensureMeta('meta[name="twitter:image"]', "name", "twitter:image", image));
    nodes.push(ensureMeta('meta[name="robots"]', "name", "robots", "index, follow"));

    // Canonical
    let canonicalEl = null;
    if (canonical) {
      canonicalEl = document.head.querySelector('link[rel="canonical"]');
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute("href", canonical);
    }

    // JSON-LD structured data
    let scriptEl = null;
    if (structuredData) {
      scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.setAttribute("data-seo", "1");
      scriptEl.text = JSON.stringify(structuredData);
      document.head.appendChild(scriptEl);
    }

    return () => {
      if (scriptEl && scriptEl.parentNode) scriptEl.parentNode.removeChild(scriptEl);
    };
  }, [title, description, canonical, image, keywords, structuredData, type]);

  return null;
};

export default SEO;
